import { authState, type IdentityServer } from '$lib/auth.svelte'
import { getServers } from '$lib/apis/server/api'
import { requestServer } from '$lib/servers.svelte'

const ROLE_FETCH_TIMEOUT_MS = 15_000

function gatewayToIdentityServer(s: any): IdentityServer {
  return {
    uid: s.uid,
    name: s.name,
    publicUrl: s.publicUrl,
    urls: s.urls,
    membership: String(s.membership ?? 'MEMBER').toUpperCase(),
    roles: [],
    ownerName: s.ownerName,
    ownerHash: s.ownerHash,
  }
}

function patchServerRoles(uid: string, roles: string[]) {
  authState.servers = authState.servers.map((s) =>
    s.uid === uid ? { ...s, roles } : s,
  )
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('timeout')), ms)
    promise.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      (err) => {
        clearTimeout(timer)
        reject(err)
      },
    )
  })
}

async function fetchDeploymentRoles(s: any): Promise<string[]> {
  try {
    const res = await withTimeout(
      requestServer(s, 'get', 'api/v1/auth/identity', null),
      ROLE_FETCH_TIMEOUT_MS,
    )
    const data = res?.data
    if (Array.isArray(data?.roles)) {
      return data.roles.filter((r: unknown) => typeof r === 'string' && r)
    }
  } catch {
    // deployment unreachable, timed out, or user not provisioned there yet
  }
  return []
}

/**
 * Fan-out role fetch across deployments, patching authState as each host
 * responds so the UI can update without waiting on slow/disconnected servers.
 */
function fanOutDeploymentRoles(listed: any[]) {
  for (const s of listed) {
    void fetchDeploymentRoles(s).then((roles) => {
      if (!authState.servers.some((x) => x.uid === s.uid)) return
      patchServerRoles(s.uid, roles)
    })
  }
}

/**
 * Fetch local entitlements for one deployment and patch authState.servers.
 * Prefer this over refreshServerAccess when only a single server's ADMIN/OWNER
 * check is needed (gateway membership is OWNER vs non-OWNER only).
 */
export async function ensureServerRoles(server: {
  uid: string
  urls?: string[]
}): Promise<string[]> {
  if (!server?.uid || !server?.urls?.length) return []
  const roles = await fetchDeploymentRoles(server)
  if (authState.servers.some((x) => x.uid === server.uid)) {
    patchServerRoles(server.uid, roles)
  } else {
    // Keep a minimal entry so callers can read roles without a full refresh.
    authState.servers = [
      ...authState.servers,
      {
        uid: server.uid,
        name: (server as { name?: string }).name ?? '',
        urls: server.urls,
        membership: 'MEMBER',
        roles,
      },
    ]
  }
  return roles
}

/**
 * Load gateway membership for each accessible server, then ask each deployment
 * for local entitlements (OWNER / ADMIN / MEMBER / …). Gateway only knows
 * OWNER vs MEMBER; promotions like Member→Admin live on the deployment.
 *
 * Returns as soon as the gateway list is applied so UIs (e.g. profile) can
 * render immediately; deployment roles are patched in progressively.
 */
export async function refreshServerAccess(): Promise<IdentityServer[]> {
  const listed = (await getServers())?.servers ?? []
  authState.servers = listed.map(gatewayToIdentityServer)
  fanOutDeploymentRoles(listed)
  return authState.servers
}
